import {
  InputText,
  Anchor,
  Button,
  DataButton,
  Icon,
  InputContainer,
  ListItem,
  TabbedDrawer,
  H4,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
  TabbedDrawerCTA,
} from "@sellar-io/components";
import { useAvailabilityTierList } from "../../../hooks/api/v2/availabilityTier/useAvailabilityTierList";
import { pluralize } from "@sellar-io/common";
import { useCustomerZonesUpdate } from "../../../hooks/api/v2/customerZones/useCustomerZonesUpdate";
import { useState } from "react";
import { Alert } from "../../../components/alert/Alert";
import { useDebounce } from "use-debounce";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { useAvailabilityTier } from "../../../hooks/api/v2/availabilityTier/useAvailabilityTier";
import { AvailabilityTierContent } from "../../availability/AvailabilityTier";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";

import { twMerge } from "tailwind-merge";

export const CustomerZoneProductCatalogue = ({
  customerZoneId,
  currentAvailabilityTierId,
}: {
  customerZoneId: number;
  currentAvailabilityTierId?: number | null;
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [search, setSearch] = useState("");
  const [sideDrawerActive, setSideDrawerActive] = useState(false);
  const [selectedAvailabilityTierId, setSelectedAvailabilityTierId] =
    useState<number>();

  const [debouncedSearch] = useDebounce(search, 500);

  const availabilityTiersQuery = useAvailabilityTierList({
    search: debouncedSearch,
    limit: 10,
    includeCounts: true,
  });

  const availabilityTiers =
    availabilityTiersQuery.data?.pages.flatMap((p) => p) ?? [];

  const customerZoneUpdate = useCustomerZonesUpdate(customerZoneId);

  const updateCustomerZone = (AvailabilityTierId: number) => {
    const availabilityTier = availabilityTiers.find(
      (at) => at.id === AvailabilityTierId,
    );

    customerZoneUpdate.mutate(
      {
        AvailabilityTierId,
      },
      {
        onError: () => {
          setError(
            `Failed to update customer zone with Product Catalogue: ${availabilityTier?.name}`,
          );
        },
      },
    );
  };

  return (
    <>
      <div className="flex flex-col gap-4 px-4">
        {error && (
          <Alert type="error" title="Failed to update" message={error} />
        )}
        <div className="flex flex-col gap-1 border-b border-zinc-300 pb-4">
          <H4 className="font-semibold">Product catalogue</H4>
          <p className="text-zinc-500">
            Product catalogues control what your customers can and cannot see on
            your storefront. Product catalogues are defined independently to the
            pricing that your customers see. For more information or help
            setting up your product catalogues, visit our{" "}
            <Anchor
              className="inline-flex items-center gap-1 text-blue-600 underline"
              href="https://intercom.help/sellario/en/articles/12008469-supplier-guide-product-catalogues"
              target="_blank"
              rel="noopener noreferrer"
            >
              help centre
              <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
            </Anchor>
            .
          </p>
        </div>
        <div className="flex items-center justify-between gap-2">
          <InputContainer className="w-full flex-none py-[0.4rem] md:max-w-[250px]">
            <InputText
              required={false}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </InputContainer>
          <div className="flex items-center gap-2">
            <DataButton
              className="flex items-center gap-1"
              border={true}
              onClick={() => navigate(ROUTES.AVAILABILITY_TIER_NEW)}
            >
              <Icon icon="PLUS" />
              New
            </DataButton>
          </div>
        </div>

        <InfiniteList
          query={availabilityTiersQuery}
          className="mb-8 flex flex-col gap-2"
        >
          {availabilityTiers.map((availabilityTier) => {
            const selected = availabilityTier.id === currentAvailabilityTierId;

            return (
              <ListItem
                key={availabilityTier.id}
                title={availabilityTier.name}
                subtitle={
                  availabilityTier.hiddenProducts === null
                    ? undefined
                    : `${availabilityTier.hiddenProducts} ${pluralize("product", availabilityTier.hiddenProducts)} hidden`
                }
                icon={selected ? "CHECK" : "TAG_MULTIPLE_OUTLINE"}
                actionText="Preview"
                onClick={() => {
                  setSelectedAvailabilityTierId(availabilityTier.id);
                  setSideDrawerActive(true);
                }}
                iconClick={() => updateCustomerZone(availabilityTier.id)}
                className={twMerge(
                  "rounded-lg bg-white p-4 hover:bg-zinc-50",
                  selected ? "border border-zinc-300 bg-zinc-50" : "",
                )}
                iconClassName={selected ? "border-zinc-800 bg-zinc-200" : ""}
              />
            );
          })}
        </InfiniteList>
      </div>
      <ProductCataloguePreview
        customerZoneId={customerZoneId}
        active={sideDrawerActive}
        setActive={(value: boolean) => {
          setSideDrawerActive(value);
          if (!sideDrawerActive) setSelectedAvailabilityTierId(undefined);
        }}
        availabilityTierId={selectedAvailabilityTierId}
        assign={() => {
          if (selectedAvailabilityTierId)
            updateCustomerZone(selectedAvailabilityTierId);
          setSideDrawerActive(false);
          if (!sideDrawerActive) setSelectedAvailabilityTierId(undefined);
        }}
      />
    </>
  );
};

const ProductCataloguePreview = ({
  customerZoneId,
  active,
  setActive,
  availabilityTierId,
  assign,
}: {
  customerZoneId: number;
  active: boolean;
  setActive: (value: boolean) => void;
  availabilityTierId?: number;
  assign: () => void;
}) => {
  return (
    <TabbedDrawer active={active} setActive={setActive} initTab={0}>
      {active && availabilityTierId && (
        <Drawer
          customerZoneId={customerZoneId}
          availabilityTierId={availabilityTierId}
          assign={assign}
        />
      )}
    </TabbedDrawer>
  );
};

const Drawer = ({
  customerZoneId,
  availabilityTierId,
  assign,
}: {
  customerZoneId: number;
  availabilityTierId: number;
  assign: () => void;
}) => {
  const navigate = useNavigate();
  const { data: availabilityTier, isLoading } =
    useAvailabilityTier(availabilityTierId);

  if (!availabilityTier || isLoading) return <></>;

  const numberOfCustomers = availabilityTier.SupplierRetailerConnections.length;

  return (
    <>
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={availabilityTier.name}
          subtitle={`Assigned to ${numberOfCustomers} ${pluralize("customer", numberOfCustomers)}`}
        />
      </TabbedDrawerHeader>
      <TabbedDrawerTab tab={0}>
        <AvailabilityTierContent
          availabilityTier={availabilityTier}
          readOnly={true}
        />
      </TabbedDrawerTab>
      <TabbedDrawerCTA className="flex-row">
        <Button
          className="flex w-full items-center gap-1"
          color="transparent"
          onClick={() =>
            navigate(ROUTES.AVAILABILITY_TIER.href({ availabilityTierId }), {
              state: { customerZoneRedirectId: customerZoneId },
            })
          }
        >
          <Icon icon="PENCIL" />
          Edit
        </Button>
        <Button className="w-full" onClick={assign}>
          Assign
        </Button>
      </TabbedDrawerCTA>
    </>
  );
};
